/**
 * BLUF-1323 — role picker page. Mirrors SelectProducerPage.
 *
 * Shown when a user's identity carries more than one role from the
 * Cognito catalogue. The callback lands them here; apiFetch also
 * routes here on any 409 ROLE_REQUIRED from a gated route (once
 * routes opt in to require-active-role.ts).
 *
 * On selection: PUT /api/me/role → refresh AuthContext → route to
 * the role's default landing.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Card, Container, Spinner } from 'react-bootstrap';
import { useAuth, type RoleOption } from './AuthContext.js';
import { apiFetch } from '../lib/api-fetch.js';
import { relativeUsedAt } from './relative-time.js';
import { useReplaceCallbackHistory } from './use-replace-callback-history.js';
import { homePathFor } from './home-path.js';

export function SelectRolePage() {
  const { activeRole, roleOptions, isLoading, refresh, logout, roleCatalogue } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useReplaceCallbackHistory('/auth/select-role');

  // A role is already active — the picker has nothing to do. Covers:
  //   1. Back-button after a successful pick (the browser tries to
  //      restore /auth/select-role even though the session moved on).
  //   2. Direct navigation / bookmark hit while signed in with a role.
  // We stay put while a picker call is in flight (loading !== null),
  // otherwise the in-progress navigation from handleSelect would race
  // this redirect and drop the user on the wrong page.
  useEffect(() => {
    if (activeRole && loading === null) {
      navigate(homePathFor(activeRole, roleCatalogue), { replace: true });
    }
  }, [activeRole, loading, navigate, roleCatalogue]);

  // /api/me hasn't landed yet — show a spinner instead of the
  // "Nothing to select" fallback (which would flash the yellow
  // warning banner during the request).
  if (isLoading) {
    return (
      <Container className="py-5 text-center" style={{ maxWidth: 520 }}>
        <Spinner animation="border" role="status" />
      </Container>
    );
  }

  // Nothing to select — role catalogue was empty or /api/me returned
  // no options. Same defence-in-depth branch as SelectProducerPage.
  if (roleOptions.length === 0) {
    return (
      <Container className="py-5" style={{ maxWidth: 520 }}>
        <Alert variant="warning">
          <Alert.Heading>Nothing to select</Alert.Heading>
          <p className="mb-2">
            Your account doesn&rsquo;t have any role options available. Try
            signing out and back in.
          </p>
          <button className="btn btn-outline-secondary" onClick={() => void logout()}>
            Sign out
          </button>
        </Alert>
      </Container>
    );
  }

  async function handleSelect(option: RoleOption): Promise<void> {
    setLoading(option.name);
    setError(null);
    try {
      const res = await apiFetch('/api/me/role', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: option.name }),
        skipAuthRedirect: true,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { message?: string }
          | null;
        setError(body?.message ?? 'Something went wrong. Please try again.');
        setLoading(null);
        return;
      }
      await refresh();
      navigate(homePathFor(option.name, roleCatalogue), { replace: true });
    } catch {
      setError('Network error. Please check your connection and try again.');
      setLoading(null);
    }
  }

  return (
    <div className="d-flex align-items-center min-vh-100 bg-light py-4">
      <Container style={{ maxWidth: 520 }}>
        <h2 className="mb-1 text-center">Which role are you working as today?</h2>
        <p className="text-secondary text-center mb-4">
          Your account has more than one role. Choose the one you&rsquo;d like
          to act under for this session.
        </p>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <div className="d-grid gap-3">
          {roleOptions.map((option, index) => (
            <Card
              key={option.name}
              role="button"
              tabIndex={0}
              className={`border shadow-sm ${loading ? 'opacity-50' : ''}`}
              style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
              onClick={() => !loading && void handleSelect(option)}
              onKeyDown={(e) => {
                if (!loading && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  void handleSelect(option);
                }
              }}
            >
              <Card.Body className="d-flex align-items-start justify-content-between">
                <div>
                  <div className="fw-semibold fs-5">
                    {option.label}
                    {index === 0 && option.lastUsedAt && (
                      <Badge bg="primary" className="ms-2 align-middle">
                        Last used
                      </Badge>
                    )}
                  </div>
                  {option.description && (
                    <small className="text-muted d-block">
                      {option.description}
                    </small>
                  )}
                  {option.lastUsedAt && (
                    <small className="text-muted">
                      {relativeUsedAt(option.lastUsedAt)}
                    </small>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2">
                  {!option.lastUsedAt && (
                    <small className="text-muted fst-italic opacity-50">
                      Not used yet
                    </small>
                  )}
                  {loading === option.name && (
                    <Spinner animation="border" size="sm" />
                  )}
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      </Container>
    </div>
  );
}
